import DOMPurify from "dompurify";

/**
 * sanitizeNoteHtml — PERMISSIVE sanitizer for AI-generated and backend-sourced HTML.
 *
 * Uses DOMPurify's smart defaults which:
 *   - ALLOW: <a>, <img>, <p>, <br>, <strong>, <b>, <em>, <i>, <u>, <ul>, <ol>, <li>,
 *            <h1-h6>, <table>, <thead>, <tbody>, <tr>, <th>, <td>, <span>, <div>,
 *            <sub>, <sup>, <hr>, <button>, <code>, <pre>, <blockquote>, <dd>, <dl>, <dt>
 *   - ALLOW: href (with javascript: blocked), class, style, src (with javascript: blocked),
 *            title, alt, target, colspan, rowspan, id
 *   - BLOCK: <script>, <style>, <meta>, <link>, <object>, <embed>
 *   - BLOCK: Event handlers (onclick, onerror, onload, onmouseover, etc.)
 *   - BLOCK: javascript: and data: URIs in href/src attributes
 *
 * This is intentionally permissive. The content flowing through dangerouslySetInnerHTML
 * in this app is primarily AI-generated from our own backend pipeline — NOT direct user
 * input. The sanitizer is a safety net against:
 *   1. Prompt-injected script tags from AI output
 *   2. XSS via javascript: URIs
 *   3. Event handler injection (onerror, onload, etc.)
 *
 * DO NOT use ALLOWED_TAGS allowlists here — it strips legitimate content that the AI
 * and our own code inject (links, images, buttons for inline variables).
 *
 * @param {string} dirty - HTML string from AI output or backend
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeNoteHtml(dirty) {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty);
}

/**
 * sanitizeNoteContentHtml — For note section bodies that contain inline variable buttons.
 *
 * Inline variables are rendered as <button onclick="editInlineValue('...')"> by
 * ChatNoteTab.js. DOMPurify's defaults strip onclick handlers (which is normally correct),
 * so this function preserves them specifically for the note section body context.
 *
 * ONLY use this for content rendered inside ChatNoteSectionBodyDefault, where inline
 * variable buttons are injected by our own frontend code (not from AI or user input).
 *
 * SECURITY NOTE: Allowing onclick opens an XSS vector if AI-generated content
 * contains <button onclick="malicious">. This is mitigated by:
 * 1. LLM system prompts that constrain output to medical content
 * 2. Backend prompt templates that don't request interactive HTML
 * 3. The onclick values are generated by our own ChatNoteTab.js code
 * TODO: Refactor inline variables to use React event delegation instead of inline onclick
 *
 * @param {string} dirty - Note section HTML (may contain inline variable buttons)
 * @returns {string} Sanitized HTML preserving inline variable functionality
 */
export function sanitizeNoteContentHtml(dirty) {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, {
    ADD_ATTR: ["onclick"],
    FORBID_TAGS: ["script", "style", "meta", "link", "object", "embed"],
  });
}

/**
 * sanitizeStrictHtml — RESTRICTIVE sanitizer for untrusted or user-generated content.
 *
 * Use for: error messages, user-authored display titles, dot phrase previews, table cell
 * content, or any context where interactive elements are not expected.
 *
 * @param {string} dirty - Untrusted HTML content
 * @returns {string} Sanitized HTML with only formatting tags allowed
 */
export function sanitizeStrictHtml(dirty) {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "span",
      "div",
      "sub",
      "sup",
      "hr",
      "a",
      "img",
      "code",
      "pre",
      "blockquote",
    ],
    ALLOWED_ATTR: ["class", "style", "href", "src", "alt", "title", "target", "colspan", "rowspan"],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Strip ALL HTML — for user-provided text that should never contain markup.
 */
export function sanitizePlainText(dirty) {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
}

/**
 * sanitizeCustomSectionHtml — STRICT allowlist sanitizer for AI-generated
 * content in user-authored custom sections.
 *
 * Threat closed: a successful prompt injection coerces the section-generation
 * LLM into emitting tags that auto-fetch external resources on render —
 * <img src>, <video poster>, <svg><image href>, inline
 * style="background-image:url()" — exfiltrating PHI from the LLM's context.
 *
 * Defense: explicit allowlist of formatting-only tags and a minimal
 * attribute allowlist. No src/href/style/poster/srcset are permitted on
 * anything. No script-context tags (script/style/iframe/object/embed) are
 * allowed. No SVG, no media tags. The clinical-content prompt templates
 * never request any of these, so the allowlist is non-lossy for legitimate
 * AI output.
 *
 * Allowlist (rather than the broader sanitizeNoteContentHtml's denylist)
 * because new escape vectors land in browsers regularly; an allowlist
 * fails closed when a new tag is invented, a denylist fails open.
 *
 * @param {string} dirty - HTML from custom-section LLM output
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeCustomSectionHtml(dirty) {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "ul",
      "ol",
      "li",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "span",
      "div",
      "sub",
      "sup",
      "hr",
      "code",
      "pre",
      "blockquote",
      "dd",
      "dl",
      "dt",
    ],
    ALLOWED_ATTR: ["colspan", "rowspan"],
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * isInstructionInfluencedSection — true when user-authored text influenced
 * this section's LLM generation (custom section OR per-section output
 * instructions), meaning a successful prompt injection could coerce
 * auto-fetching markup (<img src>, style url()) that exfiltrates PHI on
 * render.
 *
 * Strict routing triggers when the section is a custom section OR carries
 * per-section output instructions (hasCustomInstructions, set by the backend
 * design script, or the raw customInstructions field itself — belt-and-
 * suspenders in case the design script drifts and omits the flag while the
 * raw field is still present on the section object). Custom sections are
 * also detected by their customSection_ variable/prompt prefix because older
 * noteDesigns don't carry the isCustomSection flag.
 *
 * @param {Object|null} section - noteDesign section ({ variable, prompt, isCustomSection, hasCustomInstructions, customInstructions })
 * @returns {boolean}
 */
export function isInstructionInfluencedSection(section) {
  return (
    !!section &&
    (section.isCustomSection === true ||
      section.hasCustomInstructions === true ||
      !!section.customInstructions ||
      String(section.prompt || "").indexOf("customSection_") === 0 ||
      String(section.variable || "").indexOf("customSection_") === 0)
  );
}

/**
 * sanitizeStandardSectionHtml — shared routing helper for renderers that have
 * NO inline-variable buttons (PlanOfCare, ProblemList, and the read-only
 * modal render surfaces in ModalIC10OptimizerNote / ModalReviewUserAction).
 *
 * Strict allowlist (sanitizeCustomSectionHtml) when isInstructionInfluencedSection
 * is true; otherwise bare DOMPurify (sanitizeNoteHtml) — NOT sanitizeNoteContentHtml,
 * which ADD_ATTRs onclick back in for ChatNoteSectionBodyDefault's inline-variable
 * buttons that these renderers don't have.
 *
 * @param {Object|null} section - noteDesign section ({ variable, prompt, isCustomSection, hasCustomInstructions, customInstructions })
 * @param {string} dirty - HTML to sanitize
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeStandardSectionHtml(section, dirty) {
  if (!dirty) return "";
  return isInstructionInfluencedSection(section) ? sanitizeCustomSectionHtml(dirty) : sanitizeNoteHtml(dirty);
}

// Enforced argument shape for the one onclick call our own FE injects on
// inline-variable buttons (see RenderedNote.js's inline-variable substitution:
// `onclick="editInlineValue('<variable>')"`). <variable> is a noteDesign
// inlineVariables object key — observed shape is a plain identifier like
// "ageVar" — so the class is deliberately narrow ([A-Za-z0-9_]+, no colon/
// hyphen: those aren't used by any current inlineVariables key). Widen this
// (and RenderedNote's injection, if it ever needs to emit a wider charset)
// together if a real variable name needs more.
const INLINE_VAR_ONCLICK_RE = /^editInlineValue\('[A-Za-z0-9_]+'\)$/;

/**
 * sanitizeStrictWithInlineVars — sanitizeCustomSectionHtml's strict allowlist,
 * plus support for our own FE-injected inline-variable edit buttons
 * (`<button onclick="editInlineValue('...')">`, injected by RenderedNote.js).
 *
 * The onclick allowance is enforced by a DOMPurify hook, not the ALLOWED_ATTR
 * list alone: ALLOWED_ATTR just lets the attribute survive DOMPurify's own
 * checks, but the hook is what actually restricts onclick's VALUE to the
 * exact editInlineValue('<var>') shape on <button> elements, and strips
 * onclick unconditionally from every other element (including a <button>
 * whose onclick doesn't match — e.g. AI-generated or prompt-injected markup
 * that happens to include a <button onclick="...">).
 *
 * CRITICAL: DOMPurify hooks are global. The hook is added immediately before
 * this sanitize call and removed in a finally block, so it can never leak
 * into another sanitizer call in this module (or elsewhere) even if
 * DOMPurify.sanitize throws.
 *
 * @param {string} dirty
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeStrictWithInlineVars(dirty) {
  if (!dirty) return "";
  const stripUnsafeOnclick = (node) => {
    if (!node.hasAttribute || !node.hasAttribute("onclick")) return;
    if (node.tagName === "BUTTON" && INLINE_VAR_ONCLICK_RE.test(node.getAttribute("onclick"))) return;
    node.removeAttribute("onclick");
  };
  DOMPurify.addHook("afterSanitizeAttributes", stripUnsafeOnclick);
  try {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "table",
        "thead",
        "tbody",
        "tr",
        "th",
        "td",
        "span",
        "div",
        "sub",
        "sup",
        "hr",
        "code",
        "pre",
        "blockquote",
        "dd",
        "dl",
        "dt",
        "button",
      ],
      ALLOWED_ATTR: ["colspan", "rowspan", "onclick", "class"],
      ALLOW_DATA_ATTR: false,
    });
  } finally {
    // Targeted removal (entryPoint + fn) so only OUR hook is popped even if
    // another afterSanitizeAttributes hook is ever registered elsewhere.
    DOMPurify.removeHook("afterSanitizeAttributes", stripUnsafeOnclick);
  }
}

/**
 * sanitizeSectionOutputHtml — routes note-section output to the correct
 * sanitizer based on whether user-authored text influenced generation.
 *
 * Strict allowlist WITH inline-variable button support (sanitizeStrictWithInlineVars)
 * when isInstructionInfluencedSection is true — see that helper for the trigger
 * conditions. Otherwise falls back to sanitizeNoteContentHtml, which preserves
 * onclick for ChatNoteSectionBodyDefault's inline-variable buttons. ONLY use
 * this router for ChatNoteSectionBodyDefault; other renderers that don't have
 * inline-variable buttons should use sanitizeStandardSectionHtml instead.
 *
 * @param {Object|null} section - noteDesign section ({ variable, prompt, isCustomSection, hasCustomInstructions, customInstructions })
 * @param {string} dirty - HTML from LLM output
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeSectionOutputHtml(section, dirty) {
  if (!dirty) return "";
  return isInstructionInfluencedSection(section) ? sanitizeStrictWithInlineVars(dirty) : sanitizeNoteContentHtml(dirty);
}

/**
 * CATALOG_RICH_TEXT_TAGS — the only markup the peptide catalog renders.
 *
 * Shared by the editor (CatalogProductEditor) and the render surfaces
 * (PeptideProductCard) so the two can't drift: whatever the editor is willing
 * to store is exactly what the catalog is willing to display. Everything else
 * — colors, fonts, backgrounds, spans, tables, headings — is dropped so pasted
 * content adopts the platform's typography instead of the source site's.
 */
export const CATALOG_RICH_TEXT_TAGS = ["b", "strong", "i", "em", "u", "ul", "ol", "li", "br", "p"];

/**
 * sanitizeCatalogRichText — STRICT allowlist for peptide catalog rich text
 * (product description / mechanism / dosing).
 *
 * @param {string} dirty
 * @returns {string} Sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitizeCatalogRichText(dirty) {
  if (!dirty) return "";
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: CATALOG_RICH_TEXT_TAGS, ALLOWED_ATTR: [] });
}

// Structural tags carried through the FIRST pass of pastedHtmlToCatalogRichText
// only so the second pass can see where the source's line boundaries were.
// None of them survive into the stored output.
const PASTE_STRUCTURE_TAGS = [
  "div",
  "span",
  "a",
  "font",
  "small",
  "big",
  "center",
  "code",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "td",
  "th",
  "blockquote",
  "pre",
  "section",
  "article",
  "header",
  "footer",
  "main",
  "aside",
  "nav",
  "dl",
  "dt",
  "dd",
  "figure",
  "figcaption",
  "hr",
  "sub",
  "sup",
];

// Tags whose content ends a visual line in the source. Unwrapping these without
// a separator is what silently welds "Week 1" + "250mcg" into "Week 1250mcg".
const PASTE_BLOCK_TAGS = new Set([
  "DIV",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "TABLE",
  "TR",
  "BLOCKQUOTE",
  "PRE",
  "SECTION",
  "ARTICLE",
  "HEADER",
  "FOOTER",
  "MAIN",
  "ASIDE",
  "NAV",
  "DL",
  "DT",
  "DD",
  "FIGURE",
  "FIGCAPTION",
  "CENTER",
]);

// Table cells sit side by side on one line, so they need a separator that isn't
// a line break. A plain space keeps "Week 1 250mcg" readable without inventing
// punctuation that wasn't in the source.
const PASTE_CELL_TAGS = new Set(["TD", "TH"]);

const PASTE_INLINE_WRAP = { B: "b", STRONG: "strong", I: "i", EM: "em", U: "u" };

const escapeCatalogText = (str) => str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Serialize a sanitized node tree down to CATALOG_RICH_TEXT_TAGS, mapping every
// dropped block element to an explicit <br> so lines survive the unwrapping.
// Built by hand rather than by regex because the input is attacker-shaped HTML
// and the output must contain only tags we emitted ourselves.
function serializeCatalogNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    // Collapse whitespace the way HTML rendering would; the source's newlines
    // and indentation are layout, not content.
    return escapeCatalogText(node.nodeValue.replace(/\s+/g, " "));
  }
  if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
    return Array.from(node.childNodes).map(serializeCatalogNode).join("");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const tag = node.tagName;
  if (tag === "BR" || tag === "HR") return "<br>";

  const inner = Array.from(node.childNodes).map(serializeCatalogNode).join("");
  const hasContent = inner.replace(/<br>/g, "").trim() !== "";

  const wrap = PASTE_INLINE_WRAP[tag];
  if (wrap) return hasContent ? `<${wrap}>${inner}</${wrap}>` : inner;
  if (tag === "P") return hasContent ? `<p>${inner}</p>` : "";
  if (tag === "UL" || tag === "OL") return hasContent ? `<${tag.toLowerCase()}>${inner}</${tag.toLowerCase()}>` : "";
  if (tag === "LI") return hasContent ? `<li>${inner}</li>` : "";
  if (PASTE_CELL_TAGS.has(tag)) return hasContent ? `${inner.trim()} ` : "";
  if (PASTE_BLOCK_TAGS.has(tag)) return hasContent ? `${inner}<br>` : "";
  return inner; // span / a / font / thead / … — unwrap, keep the text
}

/**
 * pastedHtmlToCatalogRichText — convert clipboard HTML into catalog rich text.
 *
 * Why this exists rather than a bare sanitizeCatalogRichText() on the paste:
 * DOMPurify's KEEP_CONTENT default preserves the text inside a removed element
 * but inserts no boundary, so stripping block tags concatenates the lines they
 * separated. A pasted dosing table collapses to "Week 1250mcgWeek 2500mcg" — a
 * dose that never appeared in the source — and div-per-line pages collapse to a
 * single run. Both are the dominant shape of the source material these fields
 * hold, so paste maps block boundaries to <br> before the strict pass.
 *
 * Returns "" when the clipboard HTML carries no usable text (a copied image,
 * say), which lets callers fall back to the text/plain flavor.
 *
 * @param {string} dirty - the clipboard's text/html flavor
 * @returns {string} HTML containing only CATALOG_RICH_TEXT_TAGS
 */
export function pastedHtmlToCatalogRichText(dirty) {
  if (!dirty) return "";
  const fragment = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [...CATALOG_RICH_TEXT_TAGS, ...PASTE_STRUCTURE_TAGS],
    ALLOWED_ATTR: [],
    RETURN_DOM_FRAGMENT: true,
  });
  const html = serializeCatalogNode(fragment)
    .replace(/\s*<br>\s*/g, "<br>")
    .replace(/(?:<br>){3,}/g, "<br><br>")
    .replace(/^(?:<br>)+|(?:<br>)+$/g, "")
    .trim();
  // Final strict pass — belt and braces over our own serializer.
  return sanitizeCatalogRichText(html);
}

/**
 * plainTextToCatalogRichText — convert a text/plain clipboard flavor into
 * catalog rich text, preserving line breaks.
 *
 * @param {string} text
 * @returns {string} Escaped HTML with newlines as <br>
 */
export function plainTextToCatalogRichText(text) {
  if (!text) return "";
  // \r alone is a real line ending from some spreadsheet and terminal sources.
  return escapeCatalogText(text).replace(/\r\n|\r|\n/g, "<br>");
}
